import { cs } from '../../lib/currency';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Loader2, CalendarDays, List as ListIcon, Clock, MapPin, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
// The tenant frontend talks to the shared platform backend, so tell the
// backend which academy domain this page is served from.
const THEADER = { 'X-Tenant-Host': typeof window !== 'undefined' ? window.location.hostname : '' };

const toMin = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return h * 60 + m; };
const toHHMM = (mins) => `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
const fmt12 = (t) => { const [h, m] = t.split(':').map(Number); const ap = h >= 12 ? 'PM' : 'AM'; const hh = h % 12 || 12; return `${hh}:${String(m).padStart(2, '0')} ${ap}`; };

export default function PublicBooking() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [duration, setDuration] = useState(60);
  const [view, setView] = useState('list');
  const [avail, setAvail] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [picked, setPicked] = useState(null); // {lane_id, lane_name, start}
  const [equipment, setEquipment] = useState({}); // {id: true}
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/booking/config`, { headers: THEADER });
        if (!r.ok) { setUnavailable(true); setLoading(false); return; }
        const d = await r.json();
        setConfig(d);
        if (d.durations?.length) setDuration(d.durations.includes(60) ? 60 : d.durations[0]);
      } catch { setUnavailable(true); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadAvail = useCallback(async () => {
    setAvailLoading(true);
    try {
      const r = await fetch(`${API}/api/public/booking/availability?date=${date}`, { headers: THEADER });
      setAvail(await r.json());
    } catch { toast.error('Could not load availability'); }
    finally { setAvailLoading(false); }
  }, [date]);

  useEffect(() => { loadAvail(); }, [loadAvail]);

  // Candidate start times (every 30 min) and free/busy per lane.
  const grid = useMemo(() => {
    if (!avail || !avail.is_open) return { times: [], lanes: [] };
    const open = toMin(avail.open_time), close = toMin(avail.close_time);
    const times = [];
    for (let t = open; t + duration <= close; t += 30) times.push(t);
    const lanes = (avail.lanes || []).map((ln) => {
      const busy = (ln.booked || []).map((b) => [toMin(b.start), toMin(b.end)]);
      const slots = times.map((start) => {
        const end = start + duration;
        const free = !busy.some(([bs, be]) => start < be && end > bs);
        return { start, free };
      });
      return { ...ln, slots };
    });
    return { times, lanes };
  }, [avail, duration]);

  const equipFee = useMemo(
    () => (config?.equipment || []).filter((e) => equipment[e.id]).reduce((s, e) => s + (e.fee || 0), 0),
    [equipment, config],
  );

  const openBooking = (lane_id, lane_name, startMin) => {
    setPicked({ lane_id, lane_name, start: toHHMM(startMin) });
    setEquipment({});
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please enter your name, email and phone'); return;
    }
    setSubmitting(true);
    try {
      const eq = Object.keys(equipment).filter((k) => equipment[k]).map((id) => ({ equipment_id: id, quantity: 1 }));
      const r = await fetch(`${API}/api/public/booking/create`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...THEADER },
        body: JSON.stringify({
          lane_id: picked.lane_id, date, start_time: picked.start, duration_minutes: duration,
          equipment: eq, customer_name: form.name, customer_email: form.email, customer_phone: form.phone,
        }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Could not create booking'); setSubmitting(false); return; }
      toast.success('Booking reserved — redirecting to payment…');
      window.location.href = d.pay_url;
    } catch { toast.error('Something went wrong'); setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-7 w-7 animate-spin text-slate-400" /></div>;

  if (unavailable || !config) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="public-booking-unavailable">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-semibold text-slate-800 mb-2">Online booking isn't available</h1>
        <p className="text-slate-500 text-sm">Lane booking is not enabled for this academy. Please contact the academy directly to reserve a lane.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-booking-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <p className="text-sm text-slate-500">{config?.academy_name}</p>
          <h1 className="text-2xl font-bold text-slate-900">Book a Lane</h1>
          <p className="text-sm text-slate-500 mt-1">Pick an open slot, pay upfront, and you're set — no account needed.</p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3 mb-5 bg-white rounded-xl border border-slate-200 p-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Date</label>
            <Input type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="booking-date" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Duration</label>
            <div className="flex gap-1" data-testid="booking-duration">
              {(config?.durations || [60]).map((d) => (
                <button key={d} onClick={() => setDuration(d)}
                  className={`px-3 py-2 rounded-md text-sm border ${duration === d ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                  data-testid={`duration-${d}`}>
                  {d >= 60 ? `${d / 60}h${d % 60 ? ` ${d % 60}m` : ''}` : `${d}m`}
                </button>
              ))}
            </div>
          </div>
          <div className="ml-auto">
            <label className="block text-xs font-medium text-slate-500 mb-1">View</label>
            <div className="flex gap-1">
              <Button variant={view === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setView('list')} data-testid="view-list"><ListIcon className="h-4 w-4 mr-1" /> List</Button>
              <Button variant={view === 'calendar' ? 'default' : 'outline'} size="sm" onClick={() => setView('calendar')} data-testid="view-calendar"><CalendarDays className="h-4 w-4 mr-1" /> Calendar</Button>
            </div>
          </div>
        </div>

        {availLoading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : !avail?.is_open ? (
          <Card><CardContent className="py-12 text-center text-slate-500">The academy is closed on this date.</CardContent></Card>
        ) : view === 'list' ? (
          <div className="space-y-4" data-testid="booking-list-view">
            {grid.lanes.map((ln) => {
              const freeSlots = ln.slots.filter((s) => s.free);
              return (
                <Card key={ln.lane_id}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3"><MapPin className="h-4 w-4 text-slate-400" /><span className="font-semibold text-slate-900">{ln.lane_name}</span></div>
                    {freeSlots.length === 0 ? (
                      <p className="text-sm text-slate-400">No {duration}-minute slots available.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {freeSlots.map((s) => (
                          <button key={s.start} onClick={() => openBooking(ln.lane_id, ln.lane_name, s.start)}
                            className="px-3 py-1.5 rounded-md text-sm border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                            data-testid={`slot-${ln.lane_id}-${toHHMM(s.start)}`}>
                            {fmt12(toHHMM(s.start))}
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card data-testid="booking-calendar-view"><CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="p-2 text-left text-slate-500 font-medium sticky left-0 bg-slate-50">Time</th>
                  {grid.lanes.map((ln) => <th key={ln.lane_id} className="p-2 text-center text-slate-600 font-medium whitespace-nowrap">{ln.lane_name}</th>)}
                </tr>
              </thead>
              <tbody>
                {grid.times.map((t, ti) => (
                  <tr key={t} className="border-b last:border-0">
                    <td className="p-2 text-slate-500 whitespace-nowrap sticky left-0 bg-white">{fmt12(toHHMM(t))}</td>
                    {grid.lanes.map((ln) => {
                      const s = ln.slots[ti];
                      return (
                        <td key={ln.lane_id} className="p-1 text-center">
                          {s.free ? (
                            <button onClick={() => openBooking(ln.lane_id, ln.lane_name, t)}
                              className="w-full py-1.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 text-xs"
                              data-testid={`cal-slot-${ln.lane_id}-${toHHMM(t)}`}>Book</button>
                          ) : (
                            <div className="w-full py-1.5 rounded bg-slate-100 text-slate-300 text-xs">—</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        )}
      </div>

      {/* Booking + customer-capture dialog */}
      <Dialog open={!!picked} onOpenChange={(o) => !o && setPicked(null)}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
          <DialogHeader><DialogTitle>Confirm your booking</DialogTitle></DialogHeader>
          {picked && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 border p-3 text-sm text-slate-700 space-y-1">
                <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {picked.lane_name}</p>
                <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-slate-400" /> {date}</p>
                <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> {fmt12(picked.start)} · {duration} min</p>
              </div>

              {(config?.equipment || []).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Add-ons</p>
                  <div className="space-y-1.5">
                    {config.equipment.map((e) => (
                      <label key={e.id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`equip-${e.id}`}>
                        <span className="flex items-center gap-2 text-sm">
                          <Checkbox checked={!!equipment[e.id]} onCheckedChange={(v) => setEquipment((p) => ({ ...p, [e.id]: !!v }))} />
                          {e.name}
                        </span>
                        <span className="text-xs text-slate-500">{e.fee ? `+${cs()}${e.fee.toFixed(2)}` : 'Free'}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" />
                <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" />
                <Input placeholder="Cell phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone" />
              </div>

              {config?.cc_surcharge_enabled && (
                <p className="text-xs text-slate-500">Payment is taken upfront on the next screen. Card payments add a {config.cc_surcharge_percent}% fee; pay by bank (ACH) to avoid it (returning customers).</p>
              )}

              <Button className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" onClick={submit} disabled={submitting} data-testid="booking-continue">
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Reserving…</> : <>Continue to payment <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
